/**
 * The page every printed form uses: a toolbar (Back, Print) over a letter-size sheet laid out like the paper
 * form, black and white. The sheet's boxes: Box (label over value), Check (☐/☒), Line (a signature line).
 */
import type { ReactNode } from 'react';

export type Letterhead = { name: string; county: string | null; address: string | null; phone: string | null };

export function PrintSheet({ label, children, note, tools, back }: { label: string; children: ReactNode; note?: ReactNode; tools?: ReactNode; back?: () => void }) {
  return (
    <div className="irp-wrap pf-wrap">
      <div className="irp-tools no-print">
        <button className="btn" onClick={() => (back ? back() : history.back())}>
          ← Back
        </button>
        <span className="small muted">{note ?? 'This is how it prints. Use Print, or your browser’s print, for a paper copy or a PDF.'}</span>
        {tools}
        <button className="btn primary" onClick={() => window.print()}>
          Print
        </button>
      </div>
      <article className="pf" aria-label={label}>
        {children}
      </article>
    </div>
  );
}

/** A plain shield mark for the letterhead (not anyone's seal). */
export function Shield({ size = 0.8 }: { size?: number }) {
  return (
    <svg viewBox="0 0 100 100" style={{ width: `${size}in`, height: `${size}in` }} aria-hidden="true">
      <circle cx="50" cy="50" r="46" fill="none" stroke="#000" strokeWidth="2" />
      <path d="M50 22 l20 8 v15 c0 13-8 21-20 25-12-4-20-12-20-25 V30z" fill="none" stroke="#000" strokeWidth="2.4" />
      <path d="M50 36 l3.5 7.2 7.9 1.1-5.7 5.5 1.4 7.8L50 53.9l-7.1 3.7 1.4-7.8-5.7-5.5 7.9-1.1z" fill="#000" />
    </svg>
  );
}

/** Agency letterhead on the left, the form's title on the right. */
export function Head({ agency, title, sub, right }: { agency: Letterhead; title: string; sub?: ReactNode; right?: ReactNode }) {
  return (
    <header className="pf-head">
      <div className="pf-ag">
        <Shield />
        <div>
          <div className="pf-agname">{agency.name}</div>
          {agency.address && <div>{agency.address}</div>}
          {agency.phone && <div>{agency.phone}</div>}
        </div>
      </div>
      <div className="pf-title">
        <h1>{title}</h1>
        {sub && <div className="pf-sub">{sub}</div>}
        {right}
      </div>
    </header>
  );
}

/** One box on the form: the small caps label and what was written in it. */
export function Box({ l, v, span = 1, mono, big, className = '' }: { l: ReactNode; v?: ReactNode; span?: number; mono?: boolean; big?: boolean; className?: string }) {
  return (
    <div className={`pf-box ${className}`} style={{ gridColumn: `span ${span}` }}>
      <span className="pf-l">{l}</span>
      <span className={`pf-v${mono ? ' mono' : ''}${big ? ' big' : ''}`}>{v === '' || v == null ? ' ' : v}</span>
    </div>
  );
}

export function Check({ on, children }: { on: boolean | null | undefined; children: ReactNode }) {
  return (
    <span className="pf-check">
      <span className={`pf-cb${on ? ' on' : ''}`} aria-label={on ? 'checked' : 'not checked'}>
        {on ? '✕' : ''}
      </span>
      {children}
    </span>
  );
}

/** A signature line: the drawn signature (or e-signature words) over the line, the caption under it. */
export function Line({ caption, image, text, date, wide }: { caption: string; image?: string | null; text?: ReactNode; date?: ReactNode; wide?: boolean }) {
  return (
    <div className={`pf-line${wide ? ' wide' : ''}`}>
      <div className="pf-ink">
        {image ? <img src={image} alt={`${caption} signature`} /> : text ? <span className="pf-esig">{text}</span> : null}
      </div>
      <div className="pf-cap">
        <span>{caption}</span>
        {date !== undefined && <span>{date || 'Date'}</span>}
      </div>
    </div>
  );
}

export const fmtD = (iso: string | null | undefined) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '');
export const fmtT = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');
export const fmtDT = (iso: string | null | undefined) => (iso ? `${fmtD(iso)} ${fmtT(iso)}` : '');
export const usd = (n: number | null | undefined) => (n == null ? '' : `$${Number(n).toFixed(2)}`);
